import React, { useEffect, useState } from 'react'
import { useSearchParams, useNavigate, Link } from 'react-router-dom'
import { CheckCircle2, Loader2, LogIn, UserPlus, XCircle } from 'lucide-react'
import toast from 'react-hot-toast'
import { apiService } from '../../services/api'
import { toastError } from '../../services/errorUtils'

interface InviteInfo {
  organization_name: string
  role: string
  email: string
}

type ViewMode = 'loading' | 'error' | 'info' | 'register' | 'done'

export default function AcceptInvite() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const token = searchParams.get('token')
  const [info, setInfo] = useState<InviteInfo | null>(null)
  const [view, setView] = useState<ViewMode>('loading')
  const [errorMsg, setErrorMsg] = useState('')
  const [accepting, setAccepting] = useState(false)
  const [registering, setRegistering] = useState(false)

  // Registration form state
  const [regName, setRegName] = useState('')
  const [regEmail, setRegEmail] = useState('')
  const [regPassword, setRegPassword] = useState('')
  const [regError, setRegError] = useState('')

  const userToken = localStorage.getItem('auth_token')

  useEffect(() => {
    if (!token) {
      setErrorMsg('Link de convite inválido.')
      setView('error')
      return
    }
    apiService.getInvitationInfo(token)
      .then((res) => {
        const data = res.data?.data
        if (data) {
          setInfo(data)
          setRegEmail(data.email || '')
          setView('info')
        } else {
          setErrorMsg('Convite inválido ou expirado.')
          setView('error')
        }
      })
      .catch(() => {
        setErrorMsg('Convite inválido ou expirado.')
        setView('error')
      })
  }, [token])

  async function handleAccept() {
    if (!token) return
    setAccepting(true)
    try {
      await apiService.acceptInvitation(token)
      setView('done')
      toast.success('Convite aceito!')
      setTimeout(() => navigate('/app'), 1500)
    } catch (error) {
      toastError(error, 'Erro ao aceitar convite. Tente novamente.')
    } finally {
      setAccepting(false)
    }
  }

  async function handleRegister(e: React.FormEvent) {
    e.preventDefault()
    if (!token) return
    setRegError('')
    setRegistering(true)
    try {
      const res = await apiService.registerForInvite({
        email: regEmail,
        password: regPassword,
        name: regName,
        token
      })
      const session = res.data?.data?.session
      if (session?.access_token) {
        localStorage.setItem('auth_token', session.access_token)
      }
      if (session?.refresh_token) {
        localStorage.setItem('refresh_token', session.refresh_token)
      }
      // Registration already accepts the invite on the backend
      setView('done')
      toast.success('Conta criada e convite aceito!')
      setTimeout(() => navigate('/app'), 1500)
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: string } } }
      setRegError(axiosErr?.response?.data?.error || 'Erro ao criar conta. Tente novamente.')
    } finally {
      setRegistering(false)
    }
  }

  if (view === 'loading') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    )
  }

  if (view === 'error') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 p-4">
        <div className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-8 shadow-sm text-center">
          <XCircle className="mx-auto h-12 w-12 text-red-400" />
          <h1 className="mt-4 text-xl font-bold text-slate-900">Convite inválido</h1>
          <p className="mt-2 text-sm text-slate-500">{errorMsg}</p>
          <Link to="/login" className="btn-primary mt-6 inline-flex items-center gap-2">
            <LogIn className="h-4 w-4" /> Ir para login
          </Link>
        </div>
      </div>
    )
  }

  if (view === 'done') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 p-4">
        <div className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-8 shadow-sm text-center">
          <CheckCircle2 className="mx-auto h-12 w-12 text-green-500" />
          <h1 className="mt-4 text-xl font-bold text-slate-900">Convite aceito!</h1>
          <p className="mt-2 text-sm text-slate-500">Redirecionando para o painel...</p>
        </div>
      </div>
    )
  }

  if (view === 'register') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 p-4">
        <div className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-8 shadow-sm">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-primary-100">
            <UserPlus className="h-7 w-7 text-primary-600" />
          </div>
          <h1 className="mt-4 text-center text-xl font-bold text-slate-900">Criar conta</h1>
          <p className="mt-1 text-center text-sm text-slate-500">
            Convite para <strong>{info?.organization_name}</strong>
          </p>

          <form onSubmit={handleRegister} className="mt-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700">Nome</label>
              <input
                type="text"
                required
                value={regName}
                onChange={(e) => setRegName(e.target.value)}
                className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                placeholder="Seu nome"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700">E-mail</label>
              <input
                type="email"
                required
                value={regEmail}
                onChange={(e) => setRegEmail(e.target.value)}
                className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                placeholder="seu@email.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700">Senha</label>
              <input
                type="password"
                required
                minLength={8}
                value={regPassword}
                onChange={(e) => setRegPassword(e.target.value)}
                className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                placeholder="Mínimo 8 caracteres"
              />
            </div>

            {regError && (
              <p className="text-sm text-red-600">{regError}</p>
            )}

            <button
              type="submit"
              disabled={registering}
              className="btn-primary inline-flex w-full items-center justify-center gap-2"
            >
              {registering ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
              Criar conta e aceitar convite
            </button>
          </form>

          <button
            type="button"
            onClick={() => setView('info')}
            className="mt-4 w-full text-center text-sm text-slate-500 hover:text-slate-700"
          >
            Voltar
          </button>
        </div>
      </div>
    )
  }

  // view === 'info'
  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 p-4">
      <div className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-8 shadow-sm text-center">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-primary-100">
          <CheckCircle2 className="h-7 w-7 text-primary-600" />
        </div>
        <h1 className="mt-4 text-xl font-bold text-slate-900">Você foi convidado!</h1>
        <p className="mt-2 text-sm text-slate-500">
          Para <strong>{info?.organization_name}</strong>
        </p>
        <p className="mt-1 text-xs text-slate-400">
          Papel: {info?.role} &middot; Email: {info?.email}
        </p>

        {userToken ? (
          <button
            type="button"
            className="btn-primary mt-6 inline-flex w-full items-center justify-center gap-2"
            disabled={accepting}
            onClick={handleAccept}
          >
            {accepting ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
            Aceitar convite
          </button>
        ) : (
          <div className="mt-6 space-y-3">
            <p className="text-sm text-slate-500">Faça login ou crie uma conta para aceitar.</p>
            <Link
              to={`/login?redirect=${encodeURIComponent(`/accept-invite?token=${token}`)}`}
              className="btn-primary inline-flex w-full items-center justify-center gap-2"
            >
              <LogIn className="h-4 w-4" /> Fazer login
            </Link>
            <button
              type="button"
              className="btn-secondary inline-flex w-full items-center justify-center gap-2"
              onClick={() => setView('register')}
            >
              <UserPlus className="h-4 w-4" /> Criar conta
            </button>
          </div>
        )}
      </div>
    </div>
  )
}